import React, { useCallback, useState, useEffect } from 'react';
import { QuickTimeRec, Transportation, DepartmentSpec, ascCtaInteractionInput, VehicleRec,
    ascElementConfigurationInput, SpecData} from 'src/models';
import css from './QuickTimeCard.module.scss';
import { Button, Checkbox, FadeAndSlideTransition } from '@framework-controls/core';
import ToastBar from 'src/components/ToastBar/ToastBar';
import { DateTime } from '@reynolds/datetime';
import { strings } from 'src/localization';
import { IconHelpCircleSm } from 'src/content/icons';
import moment from 'moment';
import SpecialConsiderationMessage from '../../SpecialConsiderationMessage/SpecialConsiderationMessage';
import 'moment/min/locales.min';
import { ascCtaInteraction, getElementColor, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';
import { THEME } from 'src/constants/ThemeNames';
import { sendSdEvent, getProviderId, buildApptData } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';
import { IAppointmentState } from 'src/store/states';

interface IProps {
    qtimeSlots: QuickTimeRec[];
    id: string;
    type: string;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    checkedId: string;
    onChecked: () => void;
    infoClick: (type: string, e: any) => void;
    locale: string;
    specDeptData: DepartmentSpec;
    setCheckId: (id) => void;
    setFooterDisabled: (boolean) => void;
    theme: string;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    appointment: IAppointmentState;
}

const QuickTimeCard = (props: IProps) => {
    const [toastFlag, setToastFlag] = useState<boolean>(false);
    const [consMessageOpen, setConsMessageOpen] = useState(false);
    const [transportationSpec, setTransportationSpec] = useState<Transportation | null>(null);
    const advisorsFullArray = props.specDeptData?.Advisors !== undefined ?
        props.specDeptData?.Advisors : [];
    const transFullArray = props.specDeptData?.Transportation !== undefined ?
        props.specDeptData?.Transportation : [];

    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y' ||
        props.specData?.jaguarTrackingFlag === 'Y' ||
        props.specData?.landRoverTrackingFlag === 'Y';
    const isVW = props.specData?.vwTrackingFlag === 'Y';

    useEffect(() => {
        // Update transportation spec when props change
        const trans = props.specDeptData?.Transportation.find(trans => trans.type === props.qtimeSlots[props.checkedId]?.transFlag);
        setTransportationSpec(trans ?? null);
    }, [props.specDeptData.Transportation, props.qtimeSlots, props.checkedId]);

    const cancelHandler = useCallback(() => {
        setConsMessageOpen(false);
        props.setCheckId('');
        props.setFooterDisabled(true);
    }, [props.setCheckId, props.setFooterDisabled]);

    const proceedHandler = useCallback(() => {
        setConsMessageOpen(false);
        setToastFlag(true);
        setTimeout(setToastFlag, 2000);
    }, []);

    // Conditional render for SpecialConsiderationMessage
    const specialConsiderationMessage = consMessageOpen && transportationSpec ? (
        <SpecialConsiderationMessage
            isOpen={consMessageOpen}
            selectedTime={props.qtimeSlots[props.checkedId]?.displayTime}
            transportation={transportationSpec}
            cancelHandler={cancelHandler}
            proceedHandler={proceedHandler}
            vehicles={props.vehicles}
            selectedVehicle={props.selectedVehicle}
            theme={props.theme}
        />
    ) : null;

    // Checkbox toggle for the suggested Quick Times
    const onToggle = useCallback(() => {
        // props.qtimeSlots[props.id].checked = true;
        const currentlyChecked = props.checkedId === props.id;
        props.onChecked();
        const trans = props.specDeptData.Transportation.find(trans => trans.type === props.qtimeSlots![props.id]?.transFlag);
        const transInfo = trans?.information ?? '';
        if (!currentlyChecked && transInfo !== '') {
            setConsMessageOpen(true);
        } else {
            setToastFlag(true);
            setTimeout(setToastFlag, 2000);
        }
    }, [props.checkedId, props.id, props.onChecked, props.specDeptData.Transportation, props.qtimeSlots]);

    const checkEventHandler = (e: any) => {
        onToggle();
        const elementColor = getElementColor(e, 'fill');
        const currentlyChecked = props.checkedId === props.id;
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: 'QuickTimeSlot',
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'form',
            elementSubType: 'checkbox',
            eventAction: 'click',
            eventActionResult: currentlyChecked ? 'uncheck' : 'check',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: 'QuickTimeSlot',
            elementType: 'form',
            elementColor: elementColor,
            eventAction: 'click',
            eventActionResult: currentlyChecked ? 'uncheck' : 'check',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
        const transFlag = props.qtimeSlots[props.id].transFlag;
        const transArr = transFullArray?.find((trans) => trans.type === transFlag);
        const transLits = transArr?.title;

        if (isSDAnalyticsEnabled) {
            if (transFlag) {
                sendSdEvent('scheduleServiceTransportStart', {
                    scheduleServiceSessionId: props.sessionId,
                    scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                        props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                    scheduleServiceTransportType: transLits ?? '',
                });
            }
            if (!isVW){
                sendSdEvent('scheduleServiceApptStart', buildApptData(props.sessionId,
                    getProviderId(props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                        props.lexusMCLaunched, props.lexusTDMCLaunched), props.appointment));
            }
        }
    };

    let checkFlag = false;
    if (props.checkedId === props.id) {
        checkFlag = true;
    }
    const tempDate = new Date(props.qtimeSlots[props.id].displayDate);
    const dateValue = new DateTime(tempDate);
    // const dayofWeekLit = dateValue.getDayOfWeek();
    const displayDate = dateValue.format('MM/DD');
    const transFlag = props.qtimeSlots[props.id].transFlag;
    const transArr = transFullArray?.find((trans) => trans.type === transFlag);
    const transLits = transArr?.title;
    const advNum = props.qtimeSlots[props.id].adv;
    const advArr = advisorsFullArray?.find((advisors) => advisors.advisorNum === advNum);
    const advName = advArr?.advisorName;
    let displayLit = '';
    if (props.type === 'A') {
        displayLit = !transLits ? '' : transLits;
    } else {
        displayLit = !advName ? '' : advName;
    }
    const processingDate = dateValue.toDate();
    let processingUTCDate = moment.utc(processingDate);
    processingUTCDate = processingUTCDate.locale(props.locale);
    const dayofWeekLit = processingUTCDate.format('dddd');
    const dateMonthLit = processingUTCDate.format('MMMM').toUpperCase();

    let displayTimeData = props.qtimeSlots[props.id].displayTime;
    let message = strings.TimerDay[0].toUpperCase() + strings.TimerDay.slice(1) + ': ';
    // message += dayofWeekLit + ', ' + dateValue.get('date');
    message += dayofWeekLit + ', ';
    // message += ' ' + dateValue.getMonthOfYear() + ' ' + dateValue.toDate().getFullYear() + '\n';
    message += dateMonthLit + ' ' + dateValue.get('date') + ', ' + dateValue.toDate().getFullYear() + '\n';
    message += strings.Time + displayTimeData;
    // The message is a little different if Night Drop
    if (transFlag === 'N') {
        displayTimeData = strings.BeforeLit + displayTimeData;
        message = strings.TimerDay[0].toUpperCase() + strings.TimerDay.slice(1) + ': ';
        message += dayofWeekLit + ', ';
        message += dateMonthLit + ' ' + dateValue.get('date') + ', ' + dateValue.toDate().getFullYear() + '\n';
        message += strings.Time + strings.DropOff + props.qtimeSlots[props.id].displayTime;
    }

    const transState = transFullArray?.find((trans) => trans.type === transFlag);
    const transTitle = transState?.title ?? '';
    const onClick = useCallback((e) => {
        props.infoClick(transTitle, e);
    }, [transTitle, props.infoClick]);

    const infoData = transState?.description ?? '';
    const infoIcon = infoData !== '' && displayLit !== advName ?
        (
            <span
                id='transIcon'
                className={classNames(css.transIcon, { [css.transIconAudi]: props.theme === THEME.AUDI })}
                onClick={onClick}
            >
                <IconHelpCircleSm
                    height={15}
                    width={15}
                />
            </span>
        ) :
        null;

    const timeCardClass = classNames(
        css.timeCardDiv,
        {
            [css.fontWeightBaseText]: props.theme === 'bmw'|| props.theme === 'ford' || props.theme === 'honda' ||
            props.theme === 'infiniti' || props.theme === 'mercedesBenz' || props.theme === 'acura',
            [css.timeCardDivMazda]: props.theme === 'mazda',
            [css.timeCardDivGenesis]: props.theme === 'genesis',
            [css.timeCardDivLexus]: props.theme === 'lexus',
        },
    );


    const dowClass = classNames(
        css.dow,
        {
            [css.colorIsSecondaryColor]: props.theme === 'infiniti' || props.theme === 'mercedesBenz',
            [css.dowNissan]: props.theme === 'nissan',
            [css.dowAcura]: props.theme === 'acura',
        },
    );

    return (
        <div id={props.id} className={timeCardClass}>
            <div className={css.gridLeft} >
                <div className={css.dateDiv}>
                    <h4 id={props.theme === 'chevrolet' || props.theme === 'honda' || props.theme === 'buick' ||
                    props.theme === 'bmw' ?
                        'h4CardSecondary' : 'h4Card'} className={dowClass}>{dayofWeekLit}</h4>
                    <div className={css.dateTime}>
                        <div>{displayDate}</div>
                        <div>, </div>
                        {/* <div>{props.qtimeSlots[props.id].displayTime}</div> */}
                        <div>{displayTimeData}</div>
                    </div>
                </div>
            </div>
            <div className={css.gridCenter}>
                <div id='displayLit'>{displayLit}
                    {/* <span id='btnLit'className={css.btnLitSpan}>{transTitle}</span> */}
                    {infoIcon}
                </div>
            </div>
            <div className={css.gridRight}>
                <Button variant='atext' tooltip={strings.SelectDateTip} onClick={checkEventHandler}>
                    <div>
                        <Checkbox
                            id={'quickTime'}
                            size={'rg'}
                            variant={'primaryCkb'}
                            checked={checkFlag}
                            onChange={onToggle}>
                        </Checkbox>
                    </div>
                </Button>
            </div>
            {specialConsiderationMessage}
            <FadeAndSlideTransition
                in={toastFlag}
                timeout={{
                    enter: 100,
                    exit: 100,
                }}
                isTop={checkFlag}
                zIndex={1000}>
                <ToastBar type='Appt' message={message} theme={props.theme}/>
            </FadeAndSlideTransition>
        </div >
    );
};

export default QuickTimeCard;
